import type { DrawingContext } from '@studio/design-system/canvas';
import { pushState } from '@studio/design-system/canvas';
import { GridLines } from '#~/control/index.js';

/**
 * Draw the grid lines.
 *
 * @param context - Drawing context.
 * @param gridLines - Drawing options.
 * @param drawingOverrides - Drawing overrides.
 * @param drawingOverrides.height - Height of the drawing area.
 * @param drawingOverrides.width - Width of the drawing area.
 * @param drawingOverrides.x - X coordinate of the drawing area.
 * @param drawingOverrides.y - Y coordinate of the drawing area.
 */
export const drawLines = (
  context: DrawingContext,
  gridLines: GridLines,
  drawingOverrides?: {
    height: number;
    width: number;
    x: number;
    y: number;
  }
): void => {
  const { canvas, surface } = context;
  let { height, width } = canvas.getBoundingClientRect();
  let [x, y] = [0, 0];

  if (drawingOverrides) {
    ({ height, width, x, y } = drawingOverrides);
  }

  // Is there anything to do?
  if (gridLines === GridLines.None) {
    return;
  }

  const formula: [[number, number], [number, number]][] =
    gridLines === GridLines.Thirds
      ? [
          [
            [width / 3, 0],
            [width / 3, height]
          ],
          [
            [(width / 3) * 2, 0],
            [(width / 3) * 2, height]
          ],
          [
            [0, height / 3],
            [width, height / 3]
          ],
          [
            [0, (height / 3) * 2],
            [width, (height / 3) * 2]
          ]
        ]
      : [
          [
            [width / 2, 0],
            [width / 2, height]
          ],
          [
            [0, height / 2],
            [width, height / 2]
          ]
        ];

  pushState(context, () => {
    surface.beginPath();

    for (const [[x1, y1], [x2, y2]] of formula) {
      surface.moveTo(x + x1, y + y1);
      surface.lineTo(x + x2, y + y2);
    }

    // Stroke black outline
    surface.strokeStyle = '#000';
    surface.lineWidth = 3;
    surface.stroke();

    surface.strokeStyle = getComputedStyle(canvas).getPropertyValue('stroke');
    surface.lineWidth = 1.5;
    surface.stroke();
  });
};
